import { isAuthErrorCode, type AuthErrorCode } from '../i18n'
import { isRole, type Role } from '../lib/roles'
import type { ClientAccount } from '../data/account'
import { grantOf, type GuestGrant } from '../data/shareLinks'

export interface SessionUser {
  id: string
  email: string
  /** Platform approval to create companies; decides "Create a client" and "Add client". */
  canCreateCompanies: boolean
  /**
   * The user's own demo company while it exists; whether their next first-run create makes
   * one; whether the question at their first real account is still to ask (GRO-131, D3).
   * An older backend sends none of them, which reads as null, false and false.
   */
  demoCompanyId?: string | null
  demoCompanyPending?: boolean
  demoPromptPending?: boolean
  /**
   * An Owner's delete of a real company freezes it for an admin to restore, rather than
   * deleting it (GRO-138, D8). An older backend sends none, which reads as false.
   */
  companyDeletionHold?: boolean
}

/** One company the user belongs to, with their role in it. */
export interface Membership {
  companyId: string
  role: Role
}

/** A guest's read-only grant for one company, with the accounts its view can toggle. */
export interface GuestSession extends GuestGrant {
  accounts: ClientAccount[]
}

export type SessionResult =
  | { status: 'authenticated'; user: SessionUser; memberships: Membership[] }
  | { status: 'guest'; guest: GuestSession }
  | { status: 'unauthenticated' }
  | { status: 'unavailable' }

/** Calls only `GET /api/auth/session`, per the AuthBoundary's checking state. */
export async function fetchSession(fetchImpl: typeof fetch = fetch): Promise<SessionResult> {
  let response: Response
  try {
    response = await fetchImpl('/api/auth/session', { credentials: 'same-origin' })
  } catch {
    return { status: 'unavailable' }
  }
  if (response.status === 401) {
    return { status: 'unauthenticated' }
  }
  if (!response.ok) {
    return { status: 'unavailable' }
  }
  try {
    const body: unknown = await response.json()
    if (typeof body === 'object' && body !== null && 'guest' in body) {
      const guest = guestFrom((body as { guest: unknown }).guest)
      return guest === null ? { status: 'unavailable' } : { status: 'guest', guest }
    }
    const session = sessionFrom(body)
    // A 200 whose body is not the session contract is not proof of a session. Treating it
    // as one would mount App around an undefined user, so it settles as unavailable and
    // the boundary offers retry instead. An unknown role fails the contract the same way:
    // a control cannot be decided for a role this build does not know.
    return session === null ? { status: 'unavailable' } : { status: 'authenticated', ...session }
  } catch {
    return { status: 'unavailable' }
  }
}

function guestFrom(value: unknown): GuestSession | null {
  const grant = grantOf(value)
  const rows = (value as { accounts?: unknown } | null)?.accounts
  if (grant === null || !Array.isArray(rows)) return null
  const accounts: ClientAccount[] = []
  for (const row of rows) {
    if (typeof row !== 'object' || row === null) return null
    const { id, name, isDemo, currency, portfolioName } = row as Record<string, unknown>
    if (typeof id !== 'string' || id === '' || typeof name !== 'string' || typeof isDemo !== 'boolean') return null
    if (currency !== null && typeof currency !== 'string') return null
    if (portfolioName !== null && typeof portfolioName !== 'string') return null
    accounts.push({ id, name, isDemo, currency, portfolioName })
  }
  return { ...grant, accounts }
}

function sessionFrom(body: unknown): { user: SessionUser; memberships: Membership[] } | null {
  if (typeof body !== 'object' || body === null) return null
  const user = (body as { user?: unknown }).user
  if (typeof user !== 'object' || user === null) return null
  const { id, email, canCreateCompanies } = user as { id?: unknown; email?: unknown; canCreateCompanies?: unknown }
  if (typeof id !== 'string' || id === '' || typeof email !== 'string' || typeof canCreateCompanies !== 'boolean') return null
  const rows = (body as { memberships?: unknown }).memberships
  if (!Array.isArray(rows)) return null
  const memberships: Membership[] = []
  for (const row of rows) {
    if (typeof row !== 'object' || row === null) return null
    const { companyId, role } = row as { companyId?: unknown; role?: unknown }
    if (typeof companyId !== 'string' || !isRole(role)) return null
    memberships.push({ companyId, role })
  }
  const demo = user as { demoCompanyId?: unknown; demoCompanyPending?: unknown; demoPromptPending?: unknown; companyDeletionHold?: unknown }
  return {
    user: {
      id,
      email,
      canCreateCompanies,
      demoCompanyId: typeof demo.demoCompanyId === 'string' && demo.demoCompanyId !== '' ? demo.demoCompanyId : null,
      demoCompanyPending: demo.demoCompanyPending === true,
      demoPromptPending: demo.demoPromptPending === true,
      companyDeletionHold: demo.companyDeletionHold === true,
    },
    memberships,
  }
}

/** `POST /api/demo-prompt` with Keep, the one answer it records (GRO-131, D5). */
export async function postDemoPrompt(answer: 'keep', fetchImpl: typeof fetch = fetch): Promise<{ status: 'ok' } | { status: 'error' }> {
  try {
    const response = await fetchImpl('/api/demo-prompt', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answer }),
    })
    return response.status === 204 ? { status: 'ok' } : { status: 'error' }
  } catch {
    return { status: 'error' }
  }
}

export type StartResult =
  | { status: 'ok'; url: string }
  | { status: 'error'; code: AuthErrorCode }

/** `POST /api/auth/google/start`; on success the caller navigates to the returned URL. */
export async function startGoogleSignIn(fetchImpl: typeof fetch = fetch): Promise<StartResult> {
  let response: Response
  try {
    response = await fetchImpl('/api/auth/google/start', { method: 'POST', credentials: 'same-origin' })
  } catch {
    return { status: 'error', code: 'service_unavailable' }
  }
  if (response.ok) {
    // Reading and parsing the body can fail on their own - an interrupted response, or a
    // 200 that is not JSON - and that failure has to arrive as a result, not as a rejected
    // promise: the caller disables the button until this settles.
    let url: string | null
    try {
      url = startUrlFrom(await response.json())
    } catch {
      return { status: 'error', code: 'provider_error' }
    }
    if (url === null) return { status: 'error', code: 'provider_error' }
    return { status: 'ok', url }
  }
  return { status: 'error', code: await errorCodeFrom(response) }
}

function startUrlFrom(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null
  const url = (body as { url?: unknown }).url
  return typeof url === 'string' && url !== '' ? url : null
}

export type SignOutResult = { status: 'ok' } | { status: 'error' }

/** `POST /api/auth/sign-out`; both 204 and the guard's special-case 401 settle as signed out. */
export async function signOut(fetchImpl: typeof fetch = fetch): Promise<SignOutResult> {
  let response: Response
  try {
    response = await fetchImpl('/api/auth/sign-out', { method: 'POST', credentials: 'same-origin' })
  } catch {
    return { status: 'error' }
  }
  if (response.status === 204 || response.status === 401) {
    return { status: 'ok' }
  }
  return { status: 'error' }
}

async function errorCodeFrom(response: Response): Promise<AuthErrorCode> {
  try {
    const body = (await response.json()) as { error?: string }
    if (body.error && isAuthErrorCode(body.error)) return body.error
  } catch {
    // no JSON body to read; fall through to the generic code
  }
  return 'provider_error'
}
